import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../context/useAuth";
import apiFetch from "../api/apiFetch";
import { clearApiCache } from "../api/cache";
import { clearTokens } from "../utils/cookies";
import Logo from "../assets/images/logo.png";
import ConfirmModal from "./ConfirmModal";
import Icon from "./Icon";

const Header = ({ showMenuToggle = false, onMenuToggle = null }) => {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const searchInputRef = useRef(null);
  const { user, clearUser } = useAuth();
  const navigate = useNavigate();
  const { role, uuid } = user || {};
  const userRole = Number(role);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const homePath =
    userRole === 1 || userRole === 2 ? "/admin/dashboard" : "/home";
  const profilePath =
    userRole === 1 || userRole === 2
      ? `/admin/profile/${uuid}`
      : `/profile/${uuid}`;
  const notifyPath =
    userRole === 1 || userRole === 2 ? "/admin/notification" : "/notification";

  const handleLogout = async () => {
    try {
      await apiFetch("api/auth/logoff", { method: "POST" }, clearUser);
    } catch {
      // ignore network errors — still clear session
    } finally {
      clearTokens();
      clearApiCache();
      clearUser();
      navigate("/", { replace: true });
    }
  };

  return (
    <nav
      className={`navbar fixed-top user-navbar p-0 ${scrolled ? "scrolled" : ""}`}
    >
      <div
        className="w-100 d-flex align-items-center justify-content-between bg-white px-3 px-md-4"
        style={{ height: "var(--header-h, 68px)" }}
      >
        {/* Left: Logo */}
        <div className="d-flex align-items-center gap-2 gap-md-3">
          {showMenuToggle && (
            <button
              type="button"
              className="header-menu-toggle d-lg-none"
              onClick={onMenuToggle}
              aria-label="Open sidebar menu"
            >
              <Icon name="list" />
            </button>
          )}
          <Link
            className="navbar-brand d-flex align-items-center mb-0"
            to={homePath}
          >
            <img src={Logo} alt="Logo" className="logo-img" />
          </Link>
        </div>

        {/* Right: Search + Notification + Profile */}
        <div className="header-right">
          {/* Expandable search — always visible */}
          <div
            className={`header-search${searchOpen ? " header-search--open" : ""}`}
          >
            <button
              type="button"
              className="header-search__icon-btn"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="Toggle search"
            >
              <Icon name="search" />
            </button>
            <input
              ref={searchInputRef}
              className="header-search__input"
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onBlur={() => {
                if (!searchQuery) setSearchOpen(false);
              }}
            />
          </div>

          <Link to={notifyPath}>
            <div className="icon-circle bell-icon">
              <Icon name="bell" />
            </div>
          </Link>

          <Link to={profilePath}>
            <div className="icon-circle user-icon">
              <Icon name="person-circle" />
            </div>
          </Link>

          {!showMenuToggle && (
            <button
              type="button"
              className="icon-circle logout-icon"
              onClick={() => setShowLogoutConfirm(true)}
              aria-label="Logout"
              title="Logout"
            >
              <Icon name="box-arrow-right" />
            </button>
          )}
        </div>
      </div>

      <ConfirmModal
        show={showLogoutConfirm}
        title="Log out?"
        message="Are you sure you want to log out of your account?"
        confirmLabel="Yes, log out"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          handleLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </nav>
  );
};

export default Header;
